/**
 * Envelope wrap/unwrap helpers for saved queries.
 *
 * The SavedQueryEnvelope is split between two backend records (body + sidecar)
 * but presented to the rest of the app as a single object. These helpers do
 * the marshalling, with deliberate degradation when the sidecar is missing or
 * malformed: the script body is always recoverable, so a partially corrupt
 * record still loads as a flat `kind: "script"` and surfaces in the UI's
 * "Needs attention" bucket.
 */
import type {
  SavedQueryEnvelope,
  EnvelopeMeta,
  WrapForSaveInput,
  WrapForSaveOutput,
} from "types/savedQuery.types"

const CURRENT_SCHEMA_VERSION = 1 as const

/** Best-effort hex sha-256. Uses `crypto.subtle` when available; falls back
 *  to a small FNV-1a so non-secure contexts (local file:// previews, certain
 *  test environments) still get a reasonable change-detection hash. The hash
 *  is only used for race detection on save — collisions are tolerable. */
export async function hashBody(
  script: string,
  meta: EnvelopeMeta
): Promise<string> {
  const input = script + " " + JSON.stringify(meta)
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.subtle !== "undefined" &&
    typeof crypto.subtle.digest === "function"
  ) {
    const bytes = new TextEncoder().encode(input)
    const digest = await crypto.subtle.digest("SHA-256", bytes)
    const arr = new Uint8Array(digest)
    let hex = ""
    for (let i = 0; i < arr.length; i++) {
      hex += arr[i].toString(16).padStart(2, "0")
    }
    return hex
  }
  // Fallback: 64-bit FNV-1a, hex-encoded.
  let h1 = 0x811c9dc5
  let h2 = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    const c = input.charCodeAt(i)
    h1 = Math.imul(h1 ^ c, 0x01000193)
    h2 = Math.imul(h2 ^ ((c >> 8) & 0xff), 0x01000193)
  }
  return (
    (h1 >>> 0).toString(16).padStart(8, "0") +
    (h2 >>> 0).toString(16).padStart(8, "0")
  )
}

/** Pack script + meta into the two payloads sent to the backend.
 *
 *  The sidecar deliberately omits the script — it lives in the body half of
 *  the saved-query pair (`/queryfile-save`) and the loader pulls it from
 *  there. Duplicating it would bloat the bulk-meta-list response and was the
 *  whole reason that endpoint felt pointless. */
export async function wrapForSave(
  input: WrapForSaveInput
): Promise<WrapForSaveOutput> {
  const etag = await hashBody(input.script, input.meta)
  const sidecar: Omit<SavedQueryEnvelope, "name" | "script"> = {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    kind: input.meta.kind,
    savedAt: new Date().toISOString(),
    etag,
    meta: input.meta,
  }
  return {
    body: input.script,
    sidecar: JSON.stringify(sidecar),
    etag,
  }
}

interface UnwrapInput {
  name: string
  body: string
  /** Raw sidecar JSON from the backend, or null if no sidecar exists (legacy script). */
  sidecar: string | null
}

/** Inverse of `wrapForSave`. Returns a fully-populated envelope with corruption
 *  fallbacks: missing sidecar → flat script; malformed sidecar → flat script; sidecar
 *  with unrecognized kind → kept as-is in the envelope but treated as "corrupt" by the
 *  bucketing UI (callers detect this by checking `kind` against the recognized union). */
export async function tryUnwrap(input: UnwrapInput): Promise<SavedQueryEnvelope> {
  const { name, body, sidecar } = input
  if (!sidecar) {
    return scriptOnlyEnvelope(name, body)
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(sidecar)
  } catch {
    return scriptOnlyEnvelope(name, body)
  }
  if (!isPlainObject(parsed)) {
    return scriptOnlyEnvelope(name, body)
  }
  // Validate version and kind. Future bumps can negotiate downgrades here.
  const schemaVersion =
    typeof parsed.schemaVersion === "number" ? parsed.schemaVersion : 0
  if (schemaVersion !== CURRENT_SCHEMA_VERSION) {
    return scriptOnlyEnvelope(name, body)
  }
  const meta = parsed.meta
  if (!isPlainObject(meta) || typeof meta.kind !== "string") {
    return scriptOnlyEnvelope(name, body)
  }
  // Reconstruct envelope using the script we have on hand (not the one inside
  // the sidecar) so a body that was edited out-of-band still wins.
  const etag = await hashBody(body, meta as unknown as EnvelopeMeta)
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    kind: meta.kind as SavedQueryEnvelope["kind"],
    script: body,
    savedAt:
      typeof parsed.savedAt === "string" ? parsed.savedAt : new Date(0).toISOString(),
    etag,
    meta: meta as unknown as EnvelopeMeta,
    name,
  }
}

/** Treat a record without a usable sidecar as a flat script. Callers may surface a
 *  warning if the original kind was structured and got lost — this helper is the
 *  "always recoverable" floor, not a place to error. */
function scriptOnlyEnvelope(name: string, body: string): SavedQueryEnvelope {
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    kind: "script",
    script: body,
    savedAt: new Date(0).toISOString(),
    etag: "", // Empty etag = no race-detection baseline; first save will set one.
    meta: { kind: "script" },
    name,
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return (
    typeof v === "object" &&
    v !== null &&
    !Array.isArray(v) &&
    Object.getPrototypeOf(v) === Object.prototype
  )
}
